import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardContent, CardActions } from "@mui/material";
import { FormEditActions } from '@arelia-components/form/FormEditActions';
import { add } from 'date-fns';
import SpinnerWrapper from '@arelia-components/ui-atoms/SpinnerWrapper';
import { useNavigate } from "react-router-dom";
import { PATHS } from "src/routes";
import AddLinkDialog from "./AddLinkDialog";
import ValidationConfirmDialog from "./ValidationConfirmDialog";
import { useMessageTemplatesAPI } from '../hooks';
import CampaignTextEditor from './CampaignTextEditor';
import CampaignTextDisplay from './CampaignTextDisplay';
import CampaignTextNoText from './CampaignTextNoText';
import CampaignTextActions from './CampaignTextActions';
import { validateMessage } from './campaignTextUtils';

type Props = {
  initialText: string;
  alertText: string | undefined;
  onSave: (text: string, date: Date | null) => void;
  onSend: (text: string) => void;
  sendNowEnabled: boolean;
  scheduledDate: string | undefined;
  wasSent: boolean;
  loading: boolean;
  isRegistrationCompleted: boolean;
  isLimitExceeded: boolean;
};

export default function CampaignText({
  initialText,
  alertText,
  onSave,
  onSend,
  sendNowEnabled,
  scheduledDate,
  loading,
  isRegistrationCompleted,
  isLimitExceeded,
  wasSent
}: Props) {
  const navigate = useNavigate();
  const scheduledAt = scheduledDate ? new Date(scheduledDate) : null;
  const [text, setText] = useState(initialText);
  const [template, setTemplate] = useState('1');
  const [editing, setEditing] = useState(false);
  const [isScheduled, setIsScheduled] = useState(!!scheduledAt);
  const [selectedDate, setSelectedDate] = useState(scheduledAt);
  const minDate = add(new Date(), { hours: 1 });
  const maxDate = add(new Date(), { days: 30 });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [pendingLinkInsert, setPendingLinkInsert] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const { data: messageTemplates, isLoading: templatesLoading } = useMessageTemplatesAPI();
  const [alertClosed, setAlertClosed] = useState(false);
  const validationErrors = validateMessage(text);

  useEffect(() => {
    if (messageTemplates && !text) {
      setText(initialText || messageTemplates[template] || '');
    }
  }, [messageTemplates]);

  useEffect(() => {
    if (showValidationErrors) setShowValidationErrors(false);
  }, [text]);

  useEffect(() => {
    setAlertClosed(false);
  }, [alertText]);

  const handleAddLink = (url: string) => {
    const input = textFieldRef.current;
    if (input) {
      input.setRangeText(`[${url}]`, input.selectionStart ?? 0, input.selectionEnd ?? 0, 'end');
      setText(input.value);
    }
    setLinkDialogOpen(false);
    setPendingLinkInsert(false);
  };

  const handleSelectChange = (event: any) => {
    setTemplate(event.target.value);
    if (messageTemplates) setText(messageTemplates[event.target.value] || '');
  };

  const handleEdit = () => {
    if (isScheduled && !selectedDate) setIsScheduled(false);
    setEditing(true);
  };

  const handleCloseAlert = () => setAlertClosed(true);

  const handleReset = () => {
    setText(initialText);
    setSelectedDate(scheduledAt);
    setIsScheduled(!!scheduledAt);
    setEditing(false);
    setShowValidationErrors(false);
  };

  const handleSubmit = () => {
    const scheduledAtResult = isScheduled ? selectedDate : null;
    onSave(text, scheduledAtResult);
    setEditing(false);

    const currentValidationErrors = validateMessage(text);
    setShowValidationErrors(currentValidationErrors.length > 0);
  };

  const toggleScheduled = () => setIsScheduled(!isScheduled);

  const disableDateSelect = (date: Date) => date < minDate || date > maxDate;

  const handleSendNow = () => {
    const currentValidationErrors = validateMessage(text);
    if (currentValidationErrors.length > 0) {
      setShowValidationDialog(true);
      return;
    }
    onSend(text);
  };

  const handleValidationDialogClose = (confirmed: boolean) => {
    setShowValidationDialog(false);
    if (confirmed) onSend(text);
  };

  const handleTokenClick = (token: string) => {
    if (token === "link") {
      setPendingLinkInsert(true);
      setLinkDialogOpen(true);
      setAnchorEl(null);
      return;
    }
    const input = textFieldRef.current;
    if (!input) return;
    const insert = `[${token}]`;
    input.setRangeText(insert, input.selectionStart ?? 0, input.selectionEnd ?? 0, 'end');
    setText(input.value);
    setAnchorEl(null);
  };

  const sendNowDisabled = editing || !isRegistrationCompleted || !text || scheduledDate != null || !sendNowEnabled || isLimitExceeded;
  let buttonInfoText = '';
  if (sendNowDisabled) {
    if (!isRegistrationCompleted) {
      buttonInfoText = 'Please complete registration before sending.';
    } else if (editing) {
      buttonInfoText = 'Please save your changes before sending.';
    } else if (!text) {
      buttonInfoText = 'Please enter text before sending.';
    } else if (scheduledDate != null) {
      buttonInfoText = 'This message is scheduled to be sent at a later date.';
    } else {
      buttonInfoText = 'No fans selected to send to.';
    }
  }

  const buttonInfoClick = isRegistrationCompleted ? null : () => {
    navigate(PATHS.FANS_SETTINGS);
  };

  const cardTitle = editing ? "Edit Campaign Text" : "Campaign Message";

  return (
    <Card sx={styles.card}>
      <CardHeader
        title={cardTitle}
        action={
          <FormEditActions
            disabled={loading}
            edit={editing}
            handleEdit={handleEdit}
            handleReset={handleReset}
            handleSubmit={handleSubmit}
          />
        }
      />
      <SpinnerWrapper loading={loading}>
        <CardContent sx={styles.cardContent}>
          {editing ? (
            <CampaignTextEditor
              text={text}
              setText={setText}
              template={template}
              setTemplate={setTemplate}
              messageTemplates={messageTemplates}
              templatesLoading={templatesLoading}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              textFieldRef={textFieldRef}
              validationErrors={validationErrors}
              showValidationErrors={showValidationErrors}
              handleSelectChange={handleSelectChange}
              handleTokenClick={handleTokenClick}
              setLinkDialogOpen={setLinkDialogOpen}
              setPendingLinkInsert={setPendingLinkInsert}
            />
          ) : !!initialText ? (
            <CampaignTextDisplay
              initialText={initialText}
              alertText={alertText}
              alertClosed={alertClosed}
              handleCloseAlert={handleCloseAlert}
              validationErrors={validationErrors}
              showValidationErrors={showValidationErrors}
            />
          ) : (
            <CampaignTextNoText handleEdit={handleEdit} />
          )}
        </CardContent>
        <CardActions sx={styles.cardActions}>
          <CampaignTextActions
            editing={editing}
            isRegistrationCompleted={isRegistrationCompleted}
            isLimitExceeded={isLimitExceeded}
            isScheduled={isScheduled}
            toggleScheduled={toggleScheduled}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            minDate={minDate}
            maxDate={maxDate}
            disableDateSelect={disableDateSelect}
            sendNowDisabled={sendNowDisabled}
            handleSendNow={handleSendNow}
            buttonInfoText={buttonInfoText}
            buttonInfoClick={buttonInfoClick}
            scheduledDate={scheduledDate}
            wasSent={wasSent}
          />
        </CardActions>
      </SpinnerWrapper>
      <ValidationConfirmDialog
        open={showValidationDialog}
        validationErrors={validationErrors}
        onClose={handleValidationDialogClose}
      />
      <AddLinkDialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setPendingLinkInsert(false);
        }}
        onAdd={handleAddLink}
      />
    </Card>
  );
}

const styles = {
  card: {
    minHeight: "450px",
    maxHeight: "450px",
    height: "450px",
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: '400px',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    paddingBottom: '0!important',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '12px',
    paddingBottom: '24px',
    paddingLeft: '30px',
    paddingRight: '30px',
    flexShrink: 0,
    justifyContent: 'flex-start',
  }
};
